import { z } from "zod/v4"
import { HomeLivePinId, SpaceKeySchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { Period, PeriodState, PeriodRequest } from "./common"
import { DocumentStatus } from "../common"

export const HomeLivePinDisplayTab = z.enum(["recommend", "following"])

export type HomeLivePinDisplayTab = z.infer<typeof HomeLivePinDisplayTab>

export const HomeLivePinDisplayTabOptions = [
  { value: "recommend" as const, label: "おすすめ" },
  { value: "following" as const, label: "フォロー中" },
]

export const HomeLivePinAudience = z.enum(["all", "followers"])

export type HomeLivePinAudience = z.infer<typeof HomeLivePinAudience>

export const HomeLivePinAudienceOptions = [
  { value: "all" as const, label: "全ユーザー" },
  { value: "followers" as const, label: "フォロワーのみ" },
]

export const HomeLivePinDocumentMeta = z.object({
  space_key: SpaceKeySchema.describe("ピン留め対象のスペースID"),
  audience: HomeLivePinAudience.describe("対象ユーザー"),
  priority: z.number().int().min(1).max(99999).describe("優先度（1-99999）"),
  display_slot: z.number().int().describe("表示枠番号"),
  display_tab: HomeLivePinDisplayTab.describe("表示タブ（おすすめ/フォロー中）"),
  display_period: Period.describe("表示期間"),
  status: DocumentStatus.describe("ピン留めの状態"),
})

export type HomeLivePinDocumentMeta = z.infer<typeof HomeLivePinDocumentMeta>

export const HomeLivePinDocumentBody = z.intersection(
  HomeLivePinDocumentMeta,
  z.object({
    display_period_state: PeriodState.describe("表示期間の状態（システム自動計算）"),
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type HomeLivePinDocumentBody = z.infer<typeof HomeLivePinDocumentBody>

export type HomeLivePinDocument = HomeLivePinDocumentBody & {
  id: HomeLivePinId,
}

export class HomeLivePinDocuments {

  static toDocumentData(pin: HomeLivePinDocument): object {
    return {
      ...pin,
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): HomeLivePinDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: HomeLivePinId(doc.id),
      space_key: data["space_key"],
      audience: data["audience"],
      priority: data["priority"],
      display_slot: data["display_slot"],
      display_tab: data["display_tab"],
      display_period: data["display_period"],
      display_period_state: data["display_period_state"],
      status: data["status"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const CreateHomeLivePinRequest = z.object({
  spaceKey: SpaceKeySchema.describe("ピン留め対象のスペースID"),
  audience: HomeLivePinAudience.describe("対象ユーザー"),
  priority: z.number().int().min(1).max(99999).default(1).describe("優先度（1-99999）"),
  displaySlot: z.number().int().default(1).describe("表示枠番号"),
  displayTab: HomeLivePinDisplayTab.describe("表示タブ（おすすめ/フォロー中）"),
  displayPeriod: PeriodRequest.describe("表示期間"),
  status: DocumentStatus.describe("ピン留めの状態"),
})

export type CreateHomeLivePinRequest = z.infer<typeof CreateHomeLivePinRequest>

export const UpdateHomeLivePinRequest = z.object({
  spaceKey: SpaceKeySchema.optional().describe("ピン留め対象のスペースID"),
  audience: HomeLivePinAudience.optional().describe("対象ユーザー"),
  priority: z.number().int().min(1).max(99999).optional().describe("優先度（1-99999）"),
  displaySlot: z.number().int().optional().describe("表示枠番号"),
  displayTab: HomeLivePinDisplayTab.optional().describe("表示タブ（おすすめ/フォロー中）"),
  displayPeriod: PeriodRequest.optional().describe("表示期間"),
  status: DocumentStatus.optional().describe("ピン留めの状態"),
})

export type UpdateHomeLivePinRequest = z.infer<typeof UpdateHomeLivePinRequest>
